/**
 * AccountDeletionsAdminPage
 *
 * Admin page listing all pending account deletion requests.
 * Admins can cancel individual requests or permanently execute deletions.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardBody, CardHeader, SecondaryButton } from "../../design-system";
import { borders, interactive, surfaces, textColors, typography } from "../../design-system/tokens";
import { SuccessIcon, CheckIcon, TrashIcon, AlertIcon } from "../../design-system/icons";
import {
  fetchAccountDeletions,
  adminCancelDeletionRequest,
  adminExecuteDeletionRequest,
} from "../utils/adminApi";
import RefreshButton from "../components/RefreshButton";

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatDateTime = (isoString) => {
  if (!isoString) return "—";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return isoString;
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
};

const truncate = (str, max = 80) => {
  if (!str) return "—";
  return str.length > max ? `${str.slice(0, max)}…` : str;
};

// ─── Component ───────────────────────────────────────────────────────────────

const AccountDeletionsAdminPage = () => {
  const [requests, setRequests] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  // Per-row action state: { [id]: { loading, error, success } }
  const [cancelState, setCancelState] = useState({});
  const [executeState, setExecuteState] = useState({});

  // Confirmation modal for the destructive "Delete account" action
  const [confirmDelete, setConfirmDelete] = useState(null); // null | request object

  const loadDeletions = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError("");

    try {
      const data = await fetchAccountDeletions();
      setRequests(data.results || []);
      setCount(data.count || 0);
    } catch (err) {
      setError(err.message || "Failed to load deletion requests.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadDeletions();
  }, [loadDeletions]);

  const handleCancel = async (deletionId) => {
    setCancelState((prev) => ({
      ...prev,
      [deletionId]: { loading: true, error: "", success: "" },
    }));

    try {
      const result = await adminCancelDeletionRequest(deletionId);
      // Remove the row from the list on success.
      setRequests((prev) => prev.filter((r) => r.id !== deletionId));
      setCount((prev) => Math.max(0, prev - 1));
      setCancelState((prev) => ({
        ...prev,
        [deletionId]: { loading: false, error: "", success: result.message || "Cancelled." },
      }));
    } catch (err) {
      setCancelState((prev) => ({
        ...prev,
        [deletionId]: { loading: false, error: err.message || "Failed to cancel.", success: "" },
      }));
    }
  };

  const handleExecute = async (deletionId) => {
    setConfirmDelete(null);
    setExecuteState((prev) => ({
      ...prev,
      [deletionId]: { loading: true, error: "", success: "" },
    }));

    try {
      const result = await adminExecuteDeletionRequest(deletionId);
      setRequests((prev) => prev.filter((r) => r.id !== deletionId));
      setCount((prev) => Math.max(0, prev - 1));
      setExecuteState((prev) => ({
        ...prev,
        [deletionId]: { loading: false, error: "", success: result.message || "Deleted." },
      }));
    } catch (err) {
      setExecuteState((prev) => ({
        ...prev,
        [deletionId]: { loading: false, error: err.message || "Failed to delete.", success: "" },
      }));
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className={`min-h-screen ${surfaces.modernApp}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className={`${typography.h2} ${textColors.primary}`}>Account Deletions</h1>
          <p className={`${typography.bodySmall} ${textColors.secondary}`}>
            Pending user account deletion requests. Cancel a request to restore the user's account.
          </p>
        </div>
        <RefreshButton
          onClick={() => loadDeletions({ silent: true })}
          isRefreshing={refreshing}
        />
      </div>

      {/* Count badge */}
      {!loading && !error && (
        <div className="mb-4">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
              count > 0
                ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
            }`}
          >
            {count > 0 ? (
              `${count} pending deletion${count !== 1 ? "s" : ""}`
            ) : (
              <span className="inline-flex items-center gap-1">
                <CheckIcon size="sm" color="success" />
                No pending deletions
              </span>
            )}
          </span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400" />
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className={`rounded-2xl p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800`}>
          <p className={`${typography.body} text-red-700 dark:text-red-300`}>{error}</p>
          <button
            onClick={() => loadDeletions()}
            className="mt-2 text-sm underline text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && count === 0 && (
        <div className={`rounded-xl border ${borders.glass} ${surfaces.glass} p-12 text-center`}>
          <div className="flex items-center justify-center gap-2 mb-2">
            <SuccessIcon size="lg" color="success" />
            <p className={`${typography.h4} ${textColors.primary}`}>All clear</p>
          </div>
          <p className={`${typography.body} ${textColors.secondary}`}>
            There are no pending account deletion requests right now.
          </p>
        </div>
      )}

      {/* Table */}
      {!loading && !error && count > 0 && (
        <Card variant="glass">
          <CardHeader
            title="Pending Requests"
            subtitle={`${count} pending deletion${count !== 1 ? "s" : ""}`}
          />
          <CardBody>
            {/* Mobile cards */}
            <div className="space-y-3 lg:hidden">
              {requests.map((req) => {
                const rowCancel = cancelState[req.id] || {};
                const rowExecute = executeState[req.id] || {};
                const isRowBusy = rowCancel.loading || rowExecute.loading;

                const title = req.username || req.email_snapshot || req.user_email || `User #${req.user_id}`;

                return (
                  <div
                    key={req.id}
                    className={`rounded-2xl border ${borders.glass} ${surfaces.glass} p-4`}
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className={`${typography.body} ${textColors.primary} font-medium truncate`} title={title}>
                          {req.user_id ? (
                            <Link
                              to={`/admin/dashboard/users/${req.user_id}`}
                              className={`underline ${textColors.primary}`}
                            >
                              {title}
                            </Link>
                          ) : (
                            <span className={`${textColors.muted} italic`}>deleted</span>
                          )}
                        </div>
                        <div className={`${typography.caption} ${textColors.muted} mt-0.5`}>
                          {formatDateTime(req.requested_at)}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <div className={`${typography.caption} ${textColors.muted}`}>Email</div>
                        <div className={`${typography.bodySmall} ${textColors.primary} mt-0.5 min-w-0 truncate font-mono`}>
                          {req.email_snapshot || req.user_email || "—"}
                        </div>
                      </div>
                      <div>
                        <div className={`${typography.caption} ${textColors.muted}`}>IP</div>
                        <div className={`${typography.bodySmall} ${textColors.primary} mt-0.5 min-w-0 truncate font-mono`}>
                          {req.request_ip || "—"}
                        </div>
                      </div>
                      {req.reason && (
                        <div className="sm:col-span-2">
                          <div className={`${typography.caption} ${textColors.muted}`}>Reason</div>
                          <div className={`${typography.bodySmall} ${textColors.primary} mt-0.5 break-words whitespace-pre-wrap line-clamp-4`}>
                            {req.reason}
                          </div>
                        </div>
                      )}
                    </div>

                    {(rowCancel.error || rowExecute.error) && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                        {rowCancel.error || rowExecute.error}
                      </p>
                    )}

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end mt-3">
                      <SecondaryButton
                        onClick={() => handleCancel(req.id)}
                        disabled={isRowBusy}
                        className="w-full sm:w-auto whitespace-nowrap"
                      >
                        {rowCancel.loading ? "Cancelling…" : "Cancel request"}
                      </SecondaryButton>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(req)}
                        disabled={isRowBusy}
                        className={`inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg ${interactive.dangerBg} ${interactive.dangerHover} ${interactive.dangerText} transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap w-full sm:w-auto`}
                      >
                        <TrashIcon size="xs" color="white" />
                        {rowExecute.loading ? "Deleting…" : "Delete account"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className={`w-full text-left ${typography.bodySmall}`}>
                <thead>
                  <tr className={`border-b ${borders.glass}`}>
                    <th className="py-2 pr-4">User</th>
                    <th className="py-2 pr-4">Email</th>
                    <th className="py-2 pr-4">Reason</th>
                    <th className="py-2 pr-4">Requested</th>
                    <th className="py-2 pr-4">IP</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((req) => {
                    const rowCancel = cancelState[req.id] || {};
                    const rowExecute = executeState[req.id] || {};
                    const isRowBusy = rowCancel.loading || rowExecute.loading;

                    return (
                      <tr key={req.id} className={`border-b ${borders.glass}`}>
                        <td className="py-2 pr-4">
                          {req.user_id ? (
                            <Link
                              to={`/admin/dashboard/users/${req.user_id}`}
                              className={`underline ${textColors.primary}`}
                            >
                              {req.username || `#${req.user_id}`}
                            </Link>
                          ) : (
                            <span className={`${textColors.muted} italic`}>deleted</span>
                          )}
                        </td>
                        <td className={`py-2 pr-4 ${textColors.secondary} font-mono text-xs`}>
                          {req.email_snapshot || req.user_email || "—"}
                        </td>
                        <td className={`py-2 pr-4 ${textColors.secondary}`}>
                          <div className="max-w-[320px] truncate" title={req.reason || ""}>
                            {truncate(req.reason)}
                          </div>
                        </td>
                        <td className={`py-2 pr-4 ${textColors.secondary} text-xs whitespace-nowrap`}>
                          {formatDateTime(req.requested_at)}
                        </td>
                        <td className={`py-2 pr-4 ${textColors.secondary} font-mono text-xs`}>
                          {req.request_ip || "—"}
                        </td>
                        <td className="py-2 text-right">
                          {(rowCancel.error || rowExecute.error) && (
                            <p className="text-xs text-red-600 dark:text-red-400 mb-1">
                              {rowCancel.error || rowExecute.error}
                            </p>
                          )}
                          <div className="flex items-center justify-end gap-2">
                            <SecondaryButton
                              onClick={() => handleCancel(req.id)}
                              disabled={isRowBusy}
                              className="whitespace-nowrap"
                            >
                              {rowCancel.loading ? "Cancelling…" : "Cancel request"}
                            </SecondaryButton>
                            <button
                              type="button"
                              onClick={() => setConfirmDelete(req)}
                              disabled={isRowBusy}
                              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg ${interactive.dangerBg} ${interactive.dangerHover} ${interactive.dangerText} transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap`}
                            >
                              <TrashIcon size="xs" color="white" />
                              {rowExecute.loading ? "Deleting…" : "Delete account"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}

      {/* ─── Confirmation Modal ──────────────────────────────────────────── */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black dark:bg-dark-950 bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
          <div className={`${surfaces.glassCard} rounded-2xl p-6 max-w-md mx-4 shadow-xl`}>
            <div className="flex items-center gap-3 mb-4">
              <AlertIcon size="lg" color="danger" />
              <h3 className={`${typography.h5} ${textColors.primary}`}>
                Permanently delete account?
              </h3>
            </div>

            <p className={`${typography.bodySmall} ${textColors.secondary} mb-2`}>
              You are about to <strong>permanently delete</strong> the account for:
            </p>

            <div className={`rounded-2xl ${surfaces.tertiary} px-4 py-3 mb-4`}>
              <p className={`${typography.body} ${textColors.primary} font-semibold`}>
                {confirmDelete.username || confirmDelete.email_snapshot || `User #${confirmDelete.user_id}`}
              </p>
              <p className={`${typography.caption} ${textColors.tertiary} font-mono`}>
                {confirmDelete.email_snapshot || confirmDelete.user_email || "—"}
              </p>
            </div>

            <p className={`${typography.caption} text-red-600 dark:text-red-400 mb-6`}>
              This action is irreversible. All websites, files, and personal data will be erased.
            </p>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className={`px-4 py-2 ${typography.button} ${textColors.primary} ${surfaces.hover} rounded transition-colors`}
              >
                Go back
              </button>
              <button
                type="button"
                onClick={() => handleExecute(confirmDelete.id)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 ${typography.button} ${interactive.dangerBg} ${interactive.dangerHover} ${interactive.dangerText} rounded transition-colors`}
              >
                <TrashIcon size="sm" color="white" />
                Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default AccountDeletionsAdminPage;
